import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Briefcase, DollarSign, TrendingUp, Clock, CheckCircle, 
  Plus, Search, Filter, MoreVertical, Phone, Mail, Building, 
  Calendar, FileText, Download, ArrowRight, X, Edit, Trash2,
  ChevronDown, Activity, Target, AlertCircle, Tag, Star, Globe, MapPin, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIPELINE_STAGES = [
  { id: 'new_lead', label: 'New Lead', color: '#8b5cf6' },
  { id: 'contacted', label: 'Contacted', color: '#3b82f6' },
  { id: 'qualified', label: 'Qualified', color: '#06b6d4' },
  { id: 'proposal', label: 'Proposal', color: '#f59e0b' },
  { id: 'negotiation', label: 'Negotiation', color: '#f97316' },
  { id: 'won', label: 'Won', color: '#22c55e' },
  { id: 'lost', label: 'Lost', color: '#ef4444' },
];

const SERVICE_TAGS = [
  'Tax & Financial Management Services',
  'Project & Program Management',
  'Business Operations & Management',
  'Technical Assistance',
  'Production & Entertainment Management',
  'Logistics & Operational Management',
  'Vendor Services (Referral)',
];

const WHERE_MET_OPTIONS = [
  'Website Inquiry',
  'Networking Event',
  'Conference',
  'Referral',
  'LinkedIn',
  'Social Media',
  'Cold Call',
  'Trade Show',
  'Chamber of Commerce',
  'Workshop/Seminar',
  'Other',
];

const VENDOR_CATEGORIES = [
  'Tax & Financial Services',
  'Project Management',
  'Business Operations',
  'Technical Services',
  'Production & Entertainment',
  'Logistics & Transportation',
  'Marketing & Advertising',
  'Legal Services',
  'IT Services',
  'Consulting',
  'Office Supplies',
  'Other',
];

// ============== CRM DASHBOARD ==============
export const CRMDashboard = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [salesReport, setSalesReport] = useState(null);
  const [recentDeals, setRecentDeals] = useState([]);
  const [tasks, setTasks] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, salesRes, dealsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/sales-report`, { headers }),
        axios.get(`${API}/admin/deals`, { headers }),
        axios.get(`${API}/admin/tasks?status=pending`, { headers }),
      ]);
      setStats(statsRes.data);
      setSalesReport(salesRes.data);
      setRecentDeals(dealsRes.data.slice(0, 5));
      setTasks(tasksRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (!stats) {
    return <div className="text-white text-center py-12">Loading dashboard...</div>;
  }

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Total Revenue" value={formatCurrency(stats.total_revenue)} color="#22c55e" />
        <StatCard icon={TrendingUp} label="Pipeline Value" value={formatCurrency(stats.pipeline_value)} color="#f59e0b" />
        <StatCard icon={Users} label="Total Clients" value={stats.total_clients} subtext={`${stats.prospects} prospects, ${stats.active_clients} active`} color="#3b82f6" />
        <StatCard icon={Target} label="Open Deals" value={stats.open_deals} subtext={`${stats.won_deals} won, ${stats.lost_deals} lost`} color="#8b5cf6" />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Mail} label="New Contacts" value={stats.new_contacts} subtext={`of ${stats.total_contacts} total`} color="#FF5E00" />
        <StatCard icon={Calendar} label="Pending Bookings" value={stats.pending_bookings} subtext={`of ${stats.total_bookings} total`} color="#06b6d4" />
        <StatCard icon={CheckCircle} label="Pending Tasks" value={stats.pending_tasks} color="#f97316" />
        <StatCard icon={AlertCircle} label="Overdue Tasks" value={stats.overdue_tasks} color="#ef4444" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue */}
        <div className="bg-[#234b8a] border border-white/20 p-6">
          <h3 className="text-white font-medium mb-4">Monthly Revenue</h3>
          {salesReport && Object.keys(salesReport.monthly_revenue).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(salesReport.monthly_revenue).slice(0, 6).map(([month, value]) => (
                <div key={month} className="flex items-center gap-4">
                  <span className="text-[#b8c9e2] text-sm w-20">{month}</span>
                  <div className="flex-1 bg-[#1a3a6e] rounded-full h-4 overflow-hidden">
                    <div 
                      className="h-full bg-[#22c55e]" 
                      style={{ width: `${Math.min((value / (salesReport.total_revenue || 1)) * 100 * 3, 100)}%` }}
                    />
                  </div>
                  <span className="text-white text-sm w-24 text-right">{formatCurrency(value)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#b8c9e2] text-sm">No revenue data yet</p>
          )}
        </div>

        {/* Recent Deals */}
        <div className="bg-[#234b8a] border border-white/20 p-6">
          <h3 className="text-white font-medium mb-4">Recent Deals</h3>
          {recentDeals.length > 0 ? (
            <div className="space-y-3">
              {recentDeals.map((deal) => (
                <div key={deal.id} className="flex items-center justify-between p-3 bg-[#1a3a6e] rounded">
                  <div>
                    <p className="text-white text-sm font-medium">{deal.title}</p>
                    <p className="text-[#b8c9e2] text-xs">{deal.service_type || 'General'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white text-sm">{formatCurrency(deal.value)}</p>
                    <span 
                      className="text-xs px-2 py-0.5 rounded"
                      style={{ backgroundColor: PIPELINE_STAGES.find(s => s.id === deal.stage)?.color + '30', color: PIPELINE_STAGES.find(s => s.id === deal.stage)?.color }}
                    >
                      {PIPELINE_STAGES.find(s => s.id === deal.stage)?.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#b8c9e2] text-sm">No deals yet</p>
          )}
        </div>
      </div>

      {/* Tasks */}
      <div className="bg-[#234b8a] border border-white/20 p-6">
        <h3 className="text-white font-medium mb-4">Upcoming Tasks</h3>
        {tasks.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {tasks.map((task) => (
              <div key={task.id} className="p-3 bg-[#1a3a6e] rounded border-l-4" style={{ borderColor: task.priority === 'high' ? '#ef4444' : task.priority === 'medium' ? '#f59e0b' : '#22c55e' }}>
                <p className="text-white text-sm font-medium">{task.title}</p>
                {task.due_date && <p className="text-[#b8c9e2] text-xs mt-1">Due: {task.due_date}</p>}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#b8c9e2] text-sm">No pending tasks</p>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
  <div className="bg-[#234b8a] border border-white/20 p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[#b8c9e2] text-sm">{label}</p>
        <p className="text-2xl font-semibold text-white mt-1">{value}</p>
        {subtext && <p className="text-[#b8c9e2] text-xs mt-1">{subtext}</p>}
      </div>
      <div className="p-2 rounded" style={{ backgroundColor: color + '20' }}>
        <Icon size={20} style={{ color }} />
      </div>
    </div>
  </div>
);

// ============== PIPELINE VIEW ==============
export const PipelineView = ({ token }) => {
  const [pipeline, setPipeline] = useState({});
  const [clients, setClients] = useState([]);
  const [showDealModal, setShowDealModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPipeline();
    fetchClients();
  }, [token]);

  const fetchPipeline = async () => {
    try {
      const res = await axios.get(`${API}/admin/pipeline`, { headers });
      setPipeline(res.data);
    } catch (error) {
      toast.error('Failed to load pipeline');
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients`, { headers });
      setClients(res.data);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const updateDealStage = async (dealId, newStage) => {
    try {
      await axios.put(`${API}/admin/deals/${dealId}/stage?stage=${newStage}`, {}, { headers });
      toast.success('Deal moved');
      fetchPipeline();
    } catch (error) {
      toast.error('Failed to update deal');
    }
  };

  const handleDragStart = (e, dealId) => {
    e.dataTransfer.setData('dealId', dealId);
  };

  const handleDrop = (e, stage) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData('dealId');
    updateDealStage(dealId, stage);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount || 0);
  };

  return (
    <div data-testid="pipeline-view">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-medium text-white">Sales Pipeline</h2>
        <button
          onClick={() => { setSelectedDeal(null); setShowDealModal(true); }}
          className="bg-[#FF5E00] hover:bg-[#e05300] text-white px-4 py-2 text-sm font-medium flex items-center gap-2"
          data-testid="add-deal-btn"
        >
          <Plus size={16} /> Add Deal
        </button>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {PIPELINE_STAGES.map((stage) => {
          const deals = pipeline[stage.id] || [];
          const stageValue = deals.reduce((sum, d) => sum + (d.value || 0), 0);
          
          return (
            <div
              key={stage.id}
              className="flex-shrink-0 w-72 bg-[#1a3a6e] rounded-lg overflow-hidden"
              onDrop={(e) => handleDrop(e, stage.id)}
              onDragOver={handleDragOver}
            >
              <div className="p-3 border-b border-white/10" style={{ backgroundColor: stage.color + '20' }}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-white text-sm">{stage.label}</span>
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded text-white">{deals.length}</span>
                </div>
                <p className="text-xs mt-1" style={{ color: stage.color }}>{formatCurrency(stageValue)}</p>
              </div>
              
              <div className="p-2 space-y-2 min-h-[200px]">
                {deals.map((deal) => (
                  <div
                    key={deal.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, deal.id)}
                    onClick={() => { setSelectedDeal(deal); setShowDealModal(true); }}
                    className="p-3 bg-[#234b8a] rounded cursor-pointer hover:bg-[#2d5ba3] transition-colors"
                  >
                    <p className="text-white text-sm font-medium truncate">{deal.title}</p>
                    <p className="text-[#b8c9e2] text-xs truncate">{deal.client_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-white text-sm font-medium">{formatCurrency(deal.value)}</span>
                      {deal.expected_close_date && (
                        <span className="text-[#b8c9e2] text-xs">{deal.expected_close_date}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showDealModal && (
        <DealModal
          deal={selectedDeal}
          clients={clients}
          token={token}
          onClose={() => setShowDealModal(false)}
          onSave={() => { setShowDealModal(false); fetchPipeline(); }}
        />
      )}
    </div>
  );
};

// ============== CLIENTS LIST ==============
export const ClientsList = ({ token }) => {
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDetail, setShowClientDetail] = useState(false);
  const [detailClient, setDetailClient] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchClients();
  }, [token, filter]);

  const fetchClients = async () => {
    try {
      const params = filter !== 'all' ? `?client_type=${filter}` : '';
      const res = await axios.get(`${API}/admin/clients${params}`, { headers });
      setClients(res.data);
    } catch (error) {
      toast.error('Failed to load clients');
    }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('Delete this client and all related data?')) return;
    try {
      await axios.delete(`${API}/admin/clients/${clientId}`, { headers });
      toast.success('Client deleted');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const exportClients = () => {
    window.open(`${API}/admin/export/clients`, '_blank');
  };

  const openClientDetail = (client) => {
    setDetailClient(client);
    setShowClientDetail(true);
  };

  const filteredClients = clients.filter(c => {
    const matchesSearch = c.full_name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      (c.company_name || '').toLowerCase().includes(search.toLowerCase());
    const matchesService = !serviceFilter || (c.service_tags || []).includes(serviceFilter);
    return matchesSearch && matchesService;
  });

  return (
    <div data-testid="clients-list">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-medium text-white">Clients</h2>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#b8c9e2]" />
            <input
              type="text"
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 bg-[#1a3a6e] border border-white/20 text-white text-sm rounded focus:outline-none focus:ring-2 focus:ring-[#FF5E00]"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white text-sm rounded focus:outline-none"
          >
            <option value="all">All Clients</option>
            <option value="prospect">Prospects</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white text-sm rounded focus:outline-none"
          >
            <option value="">All Services</option>
            {SERVICE_TAGS.map(tag => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </select>
          <button
            onClick={exportClients}
            className="px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white text-sm rounded hover:bg-[#234b8a] flex items-center gap-2"
          >
            <Download size={16} /> Export
          </button>
          <button
            onClick={() => { setSelectedClient(null); setShowModal(true); }}
            className="bg-[#FF5E00] hover:bg-[#e05300] text-white px-4 py-2 text-sm font-medium flex items-center gap-2"
            data-testid="add-client-btn"
          >
            <Plus size={16} /> Add Client
          </button>
        </div>
      </div>

      <div className="bg-[#234b8a] border border-white/20 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1a3a6e]">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Name</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Company</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Email</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Where Met</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Services</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client.id} className="border-t border-white/10 hover:bg-[#2d5ba3]/30 cursor-pointer" onClick={() => openClientDetail(client)}>
                  <td className="px-4 py-3 text-white">{client.full_name}</td>
                  <td className="px-4 py-3 text-[#b8c9e2]">{client.company_name || '-'}</td>
                  <td className="px-4 py-3 text-[#b8c9e2]">{client.email}</td>
                  <td className="px-4 py-3">
                    {client.where_met && (
                      <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400">
                        {client.where_met}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(client.service_tags || []).slice(0, 2).map((tag, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded bg-[#FF5E00]/20 text-[#FF5E00]">
                          {tag.split(' ')[0]}
                        </span>
                      ))}
                      {(client.service_tags || []).length > 2 && (
                        <span className="text-xs text-[#b8c9e2]">+{client.service_tags.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded ${
                      client.client_type === 'active' ? 'bg-green-500/20 text-green-400' :
                      client.client_type === 'prospect' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {client.client_type}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setSelectedClient(client); setShowModal(true); }}
                        className="p-1 text-[#b8c9e2] hover:text-white"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => deleteClient(client.id)}
                        className="p-1 text-[#b8c9e2] hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredClients.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-[#b8c9e2]">No clients found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <ClientModal
          client={selectedClient}
          token={token}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchClients(); }}
        />
      )}

      {showClientDetail && detailClient && (
        <ClientDetailModal
          client={detailClient}
          token={token}
          onClose={() => setShowClientDetail(false)}
          onUpdate={() => { fetchClients(); }}
        />
      )}
    </div>
  );
};

// ============== VENDOR DIRECTORY ==============
export const VendorDirectory = ({ token }) => {
  const [vendors, setVendors] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchVendors();
  }, [token, categoryFilter]);

  const fetchVendors = async () => {
    try {
      const params = categoryFilter ? `?category=${encodeURIComponent(categoryFilter)}` : '';
      const res = await axios.get(`${API}/admin/vendors${params}`, { headers });
      setVendors(res.data);
    } catch (error) {
      toast.error('Failed to load vendors');
    }
  };

  const deleteVendor = async (vendorId) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await axios.delete(`${API}/admin/vendors/${vendorId}`, { headers });
      toast.success('Vendor deleted');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const exportVendors = () => {
    window.open(`${API}/admin/export/vendors`, '_blank');
  };

  const filteredVendors = vendors.filter(v =>
    v.company_name.toLowerCase().includes(search.toLowerCase()) ||
    (v.contact_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (v.services_provided || []).some(s => s.toLowerCase().includes(search.toLowerCase()))
  );

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} size={14} className={i < (rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-500'} />
    ));
  };

  return (
    <div data-testid="vendor-directory">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-medium text-white">Vendor Directory</h2>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#b8c9e2]" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 bg-[#1a3a6e] border border-white/20 text-white text-sm rounded focus:outline-none focus:ring-2 focus:ring-[#FF5E00]"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white text-sm rounded focus:outline-none"
          >
            <option value="">All Categories</option>
            {VENDOR_CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <button
            onClick={exportVendors}
            className="px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white text-sm rounded hover:bg-[#234b8a] flex items-center gap-2"
          >
            <Download size={16} /> Export
          </button>
          <button
            onClick={() => { setSelectedVendor(null); setShowModal(true); }}
            className="bg-[#FF5E00] hover:bg-[#e05300] text-white px-4 py-2 text-sm font-medium flex items-center gap-2"
            data-testid="add-vendor-btn"
          >
            <Plus size={16} /> Add Vendor
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredVendors.map((vendor) => (
          <div key={vendor.id} className="bg-[#234b8a] border border-white/20 p-4 hover:border-white/40 transition-colors">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-medium">{vendor.company_name}</h3>
                {vendor.contact_name && <p className="text-[#b8c9e2] text-sm">{vendor.contact_name}</p>}
              </div>
              <span className={`text-xs px-2 py-1 rounded ${
                vendor.status === 'preferred' ? 'bg-green-500/20 text-green-400' :
                vendor.status === 'active' ? 'bg-blue-500/20 text-blue-400' :
                'bg-gray-500/20 text-gray-400'
              }`}>
                {vendor.status}
              </span>
            </div>
            
            <div className="space-y-2 text-sm mb-3">
              <span className="inline-block px-2 py-0.5 bg-[#1a3a6e] text-[#FF5E00] rounded text-xs">
                {vendor.category}
              </span>
              
              {vendor.rating && (
                <div className="flex items-center gap-1">
                  {renderStars(vendor.rating)}
                </div>
              )}
              
              {vendor.phone && (
                <p className="flex items-center gap-2 text-[#b8c9e2]">
                  <Phone size={14} /> {vendor.phone}
                </p>
              )}
              {vendor.email && (
                <p className="flex items-center gap-2 text-[#b8c9e2]">
                  <Mail size={14} /> {vendor.email}
                </p>
              )}
              {vendor.website && (
                <p className="flex items-center gap-2 text-[#b8c9e2]">
                  <Globe size={14} /> 
                  <a href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`} target="_blank" rel="noopener noreferrer" className="text-[#FF5E00] hover:underline truncate">
                    {vendor.website}
                  </a>
                </p>
              )}
              {(vendor.city || vendor.state) && (
                <p className="flex items-center gap-2 text-[#b8c9e2]">
                  <MapPin size={14} /> {[vendor.city, vendor.state].filter(Boolean).join(', ')}
                </p>
              )}
            </div>
            
            {vendor.services_provided && vendor.services_provided.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {vendor.services_provided.map((service, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-[#1a3a6e] text-[#b8c9e2] rounded">
                    {service}
                  </span>
                ))}
              </div>
            )}
            
            <div className="flex items-center gap-2 pt-3 border-t border-white/10">
              <button
                onClick={() => { setSelectedVendor(vendor); setShowModal(true); }}
                className="flex-1 py-2 text-sm text-white bg-[#1a3a6e] hover:bg-[#2d5ba3] rounded flex items-center justify-center gap-2"
              >
                <Edit size={14} /> Edit
              </button>
              <button
                onClick={() => deleteVendor(vendor.id)}
                className="py-2 px-3 text-sm text-red-400 hover:bg-red-500/10 rounded"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {filteredVendors.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#b8c9e2]">
            No vendors found. Add your first vendor!
          </div>
        )}
      </div>

      {showModal && (
        <VendorModal
          vendor={selectedVendor}
          token={token}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchVendors(); }}
        />
      )}
    </div>
  );
};

// ============== TASKS LIST ==============
export const TasksList = ({ token }) => {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [showModal, setShowModal] = useState(false);
  const [clients, setClients] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchTasks();
    fetchClients();
  }, [token, filter]);

  const fetchTasks = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/admin/tasks${params}`, { headers });
      setTasks(res.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients`, { headers });
      setClients(res.data);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const toggleTaskStatus = async (task) => {
    try {
      const newStatus = task.status === 'pending' ? 'completed' : 'pending';
      await axios.put(`${API}/admin/tasks/${task.id}`, { status: newStatus }, { headers });
      toast.success('Task updated');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/admin/tasks/${taskId}`, { headers });
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  return (
    <div data-testid="tasks-list">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-medium text-white">Tasks</h2>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white text-sm rounded focus:outline-none"
          >
            <option value="all">All Tasks</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
          <button
            onClick={() => setShowModal(true)}
            className="bg-[#FF5E00] hover:bg-[#e05300] text-white px-4 py-2 text-sm font-medium flex items-center gap-2"
            data-testid="add-task-btn"
          >
            <Plus size={16} /> Add Task
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {tasks.map((task) => {
          const client = clients.find(c => c.id === task.client_id);
          return (
            <div
              key={task.id}
              className={`p-4 bg-[#234b8a] border border-white/20 flex items-start gap-4 ${task.status === 'completed' ? 'opacity-60' : ''}`}
            >
              <button
                onClick={() => toggleTaskStatus(task)}
                className={`mt-1 w-5 h-5 rounded border flex items-center justify-center ${
                  task.status === 'completed' ? 'bg-green-500 border-green-500' : 'border-white/40'
                }`}
              >
                {task.status === 'completed' && <CheckCircle size={14} className="text-white" />}
              </button>
              
              <div className="flex-1">
                <p className={`text-white font-medium ${task.status === 'completed' ? 'line-through' : ''}`}>{task.title}</p>
                {task.description && <p className="text-[#b8c9e2] text-sm mt-1">{task.description}</p>}
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#b8c9e2]">
                  {task.due_date && (
                    <span className="flex items-center gap-1">
                      <Calendar size={12} /> {task.due_date}
                    </span>
                  )}
                  {client && (
                    <span className="flex items-center gap-1">
                      <Users size={12} /> {client.full_name}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded ${
                    task.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                    task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-green-500/20 text-green-400'
                  }`}>
                    {task.priority}
                  </span>
                </div>
              </div>
              
              <button
                onClick={() => deleteTask(task.id)}
                className="p-1 text-[#b8c9e2] hover:text-red-400"
              >
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
        {tasks.length === 0 && (
          <div className="text-center py-8 text-[#b8c9e2]">No tasks found</div>
        )}
      </div>

      {showModal && (
        <TaskModal
          clients={clients}
          token={token}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchTasks(); }}
        />
      )}
    </div>
  );
};

// ============== ACTIVITIES LIST ==============
export const ActivitiesList = ({ token }) => {
  const [activities, setActivities] = useState([]);
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchActivities();
    fetchClients();
  }, [token]);

  const fetchActivities = async () => {
    try {
      const res = await axios.get(`${API}/admin/activities`, { headers });
      setActivities(res.data);
    } catch (error) {
      toast.error('Failed to load activities');
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients`, { headers });
      setClients(res.data);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'call': return Phone;
      case 'email': return Mail;
      case 'meeting': return Users;
      default: return FileText;
    }
  };

  return (
    <div data-testid="activities-list">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-medium text-white">Activity Log</h2>
        <button
          onClick={() => setShowModal(true)}
          className="bg-[#FF5E00] hover:bg-[#e05300] text-white px-4 py-2 text-sm font-medium flex items-center gap-2"
          data-testid="add-activity-btn"
        >
          <Plus size={16} /> Log Activity
        </button>
      </div>

      <div className="space-y-3">
        {activities.map((activity) => {
          const Icon = getActivityIcon(activity.activity_type);
          const client = clients.find(c => c.id === activity.client_id);
          return (
            <div key={activity.id} className="p-4 bg-[#234b8a] border border-white/20 flex gap-4">
              <div className="p-2 bg-[#1a3a6e] rounded">
                <Icon size={20} className="text-[#FF5E00]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white font-medium capitalize">{activity.activity_type}</span>
                  {client && <span className="text-[#b8c9e2] text-sm">with {client.full_name}</span>}
                </div>
                <p className="text-[#b8c9e2] text-sm">{activity.description}</p>
                <p className="text-[#b8c9e2] text-xs mt-2">
                  {new Date(activity.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          );
        })}
        {activities.length === 0 && (
          <div className="text-center py-8 text-[#b8c9e2]">No activities logged yet</div>
        )}
      </div>

      {showModal && (
        <ActivityModal
          clients={clients}
          token={token}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchActivities(); }}
        />
      )}
    </div>
  );
};

// ============== CLIENT DETAIL MODAL ==============
const ClientDetailModal = ({ client, token, onClose, onUpdate }) => {
  const [activities, setActivities] = useState([]);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [activityForm, setActivityForm] = useState({ activity_type: 'email', subject: '', description: '' });
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchActivities();
  }, [client.id]);

  const fetchActivities = async () => {
    try {
      const res = await axios.get(`${API}/admin/activities?client_id=${client.id}`, { headers });
      setActivities(res.data);
    } catch (error) {
      console.error('Failed to load activities');
    }
  };

  const openEmailClient = () => {
    const subject = encodeURIComponent(emailForm.subject || `Following up - ${client.company_name || client.full_name}`);
    const body = encodeURIComponent(emailForm.body || `Hi ${client.full_name.split(' ')[0]},\n\n`);
    window.open(`mailto:${client.email}?subject=${subject}&body=${body}`, '_blank');
    setShowEmailForm(false);
  };

  const logActivity = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const description = activityForm.subject 
        ? `Subject: ${activityForm.subject}\n\n${activityForm.description}`
        : activityForm.description;
      
      await axios.post(`${API}/admin/activities`, {
        client_id: client.id,
        activity_type: activityForm.activity_type,
        description: description
      }, { headers });
      
      toast.success('Activity logged');
      setActivityForm({ activity_type: 'email', subject: '', description: '' });
      setShowActivityForm(false);
      fetchActivities();
    } catch (error) {
      toast.error('Failed to log activity');
    } finally {
      setLoading(false);
    }
  };

  const quickCall = () => {
    window.open(`tel:${client.phone}`, '_self');
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'call': return Phone;
      case 'email': return Mail;
      case 'meeting': return Users;
      default: return FileText;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a3a6e] border border-white/20 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/20">
          <div>
            <h3 className="text-xl font-medium text-white">{client.full_name}</h3>
            {client.company_name && <p className="text-[#b8c9e2] text-sm">{client.company_name}</p>}
          </div>
          <button onClick={onClose} className="text-[#b8c9e2] hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left Column - Client Info */}
            <div className="lg:col-span-1 space-y-4">
              {/* Contact Info Card */}
              <div className="bg-[#234b8a] border border-white/20 p-4 rounded">
                <h4 className="text-white font-medium mb-3">Contact Information</h4>
                <div className="space-y-2 text-sm">
                  <p className="flex items-center gap-2 text-[#b8c9e2]">
                    <Mail size={14} className="text-[#FF5E00]" /> {client.email}
                  </p>
                  <p className="flex items-center gap-2 text-[#b8c9e2]">
                    <Phone size={14} className="text-[#FF5E00]" /> {client.phone}
                  </p>
                  {(client.city || client.state) && (
                    <p className="flex items-center gap-2 text-[#b8c9e2]">
                      <MapPin size={14} className="text-[#FF5E00]" /> {[client.city, client.state].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
                
                {/* Quick Actions */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setShowEmailForm(true)}
                    className="flex-1 py-2 px-3 bg-[#FF5E00] hover:bg-[#e05300] text-white text-sm rounded flex items-center justify-center gap-2"
                  >
                    <Mail size={14} /> Email
                  </button>
                  <button
                    onClick={quickCall}
                    className="flex-1 py-2 px-3 bg-[#1a3a6e] hover:bg-[#2d5ba3] text-white text-sm rounded flex items-center justify-center gap-2 border border-white/20"
                  >
                    <Phone size={14} /> Call
                  </button>
                </div>
              </div>

              {/* Tags Card */}
              <div className="bg-[#234b8a] border border-white/20 p-4 rounded">
                <h4 className="text-white font-medium mb-3">Details</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-[#b8c9e2] text-xs mb-1">Status</p>
                    <span className={`text-xs px-2 py-1 rounded ${
                      client.client_type === 'active' ? 'bg-green-500/20 text-green-400' :
                      client.client_type === 'prospect' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {client.client_type}
                    </span>
                  </div>
                  {client.where_met && (
                    <div>
                      <p className="text-[#b8c9e2] text-xs mb-1">Where Met</p>
                      <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400">
                        {client.where_met}
                      </span>
                    </div>
                  )}
                  {client.service_tags && client.service_tags.length > 0 && (
                    <div>
                      <p className="text-[#b8c9e2] text-xs mb-1">Services</p>
                      <div className="flex flex-wrap gap-1">
                        {client.service_tags.map((tag, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded bg-[#FF5E00]/20 text-[#FF5E00]">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes Card */}
              {client.notes && (
                <div className="bg-[#234b8a] border border-white/20 p-4 rounded">
                  <h4 className="text-white font-medium mb-2">Notes</h4>
                  <p className="text-[#b8c9e2] text-sm whitespace-pre-wrap">{client.notes}</p>
                </div>
              )}
            </div>

            {/* Right Column - Activity Timeline */}
            <div className="lg:col-span-2">
              <div className="bg-[#234b8a] border border-white/20 p-4 rounded">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-white font-medium">Activity Timeline</h4>
                  <button
                    onClick={() => setShowActivityForm(true)}
                    className="px-3 py-1.5 bg-[#FF5E00] hover:bg-[#e05300] text-white text-sm rounded flex items-center gap-2"
                  >
                    <Plus size={14} /> Log Activity
                  </button>
                </div>

                {/* Activity Form */}
                {showActivityForm && (
                  <form onSubmit={logActivity} className="mb-4 p-4 bg-[#1a3a6e] rounded border border-white/20">
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <select
                        value={activityForm.activity_type}
                        onChange={(e) => setActivityForm({...activityForm, activity_type: e.target.value})}
                        className="px-3 py-2 bg-[#234b8a] border border-white/20 text-white text-sm rounded"
                      >
                        <option value="email">Email</option>
                        <option value="call">Phone Call</option>
                        <option value="meeting">Meeting</option>
                        <option value="note">Note</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Subject (optional)"
                        value={activityForm.subject}
                        onChange={(e) => setActivityForm({...activityForm, subject: e.target.value})}
                        className="px-3 py-2 bg-[#234b8a] border border-white/20 text-white text-sm rounded"
                      />
                    </div>
                    <textarea
                      placeholder="Description / Notes..."
                      value={activityForm.description}
                      onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                      required
                      rows={3}
                      className="w-full px-3 py-2 bg-[#234b8a] border border-white/20 text-white text-sm rounded resize-none mb-3"
                    />
                    <div className="flex gap-2 justify-end">
                      <button type="button" onClick={() => setShowActivityForm(false)} className="px-3 py-1.5 text-[#b8c9e2] hover:text-white text-sm">
                        Cancel
                      </button>
                      <button type="submit" disabled={loading} className="px-3 py-1.5 bg-[#FF5E00] text-white text-sm rounded disabled:opacity-50">
                        {loading ? 'Saving...' : 'Save Activity'}
                      </button>
                    </div>
                  </form>
                )}

                {/* Activity List */}
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {activities.length > 0 ? activities.map((activity) => {
                    const Icon = getActivityIcon(activity.activity_type);
                    return (
                      <div key={activity.id} className="flex gap-3 p-3 bg-[#1a3a6e] rounded">
                        <div className="p-2 bg-[#234b8a] rounded h-fit">
                          <Icon size={16} className="text-[#FF5E00]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white text-sm font-medium capitalize">{activity.activity_type}</span>
                            <span className="text-[#b8c9e2] text-xs">
                              {new Date(activity.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-[#b8c9e2] text-sm whitespace-pre-wrap">{activity.description}</p>
                        </div>
                      </div>
                    );
                  }) : (
                    <p className="text-center text-[#b8c9e2] py-8">No activities logged yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Email Compose Modal */}
        {showEmailForm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-[#1a3a6e] border border-white/20 w-full max-w-md p-4 rounded">
              <h4 className="text-white font-medium mb-4">Compose Email to {client.full_name}</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-[#b8c9e2] mb-1 block">To</label>
                  <input
                    type="text"
                    value={client.email}
                    disabled
                    className="w-full px-3 py-2 bg-[#234b8a] border border-white/20 text-[#b8c9e2] text-sm rounded"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#b8c9e2] mb-1 block">Subject</label>
                  <input
                    type="text"
                    placeholder="Email subject..."
                    value={emailForm.subject}
                    onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                    className="w-full px-3 py-2 bg-[#234b8a] border border-white/20 text-white text-sm rounded"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#b8c9e2] mb-1 block">Message</label>
                  <textarea
                    placeholder="Start typing your message..."
                    value={emailForm.body}
                    onChange={(e) => setEmailForm({...emailForm, body: e.target.value})}
                    rows={5}
                    className="w-full px-3 py-2 bg-[#234b8a] border border-white/20 text-white text-sm rounded resize-none"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4 justify-end">
                <button onClick={() => setShowEmailForm(false)} className="px-4 py-2 text-[#b8c9e2] hover:text-white text-sm">
                  Cancel
                </button>
                <button
                  onClick={openEmailClient}
                  className="px-4 py-2 bg-[#FF5E00] hover:bg-[#e05300] text-white text-sm rounded flex items-center gap-2"
                >
                  <ExternalLink size={14} /> Open in Email App
                </button>
              </div>
              <p className="text-[#b8c9e2] text-xs mt-3">
                This will open your default email app. After sending, come back and log the activity to track it in the CRM.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== MODALS ==============

const ClientModal = ({ client, token, onClose, onSave }) => {
  const [form, setForm] = useState(client || {
    full_name: '', email: '', phone: '', company_name: '', address: '',
    city: '', state: '', zip_code: '', client_type: 'prospect', source: '', 
    where_met: '', service_tags: [], notes: ''
  });
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (client) {
        await axios.put(`${API}/admin/clients/${client.id}`, form, { headers });
        toast.success('Client updated');
      } else {
        await axios.post(`${API}/admin/clients`, form, { headers });
        toast.success('Client created');
      }
      onSave();
    } catch (error) {
      toast.error('Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  const toggleServiceTag = (tag) => {
    const tags = form.service_tags || [];
    if (tags.includes(tag)) {
      setForm({ ...form, service_tags: tags.filter(t => t !== tag) });
    } else {
      setForm({ ...form, service_tags: [...tags, tag] });
    }
  };

  return (
    <Modal title={client ? 'Edit Client' : 'Add Client'} onClose={onClose} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input label="Full Name *" value={form.full_name} onChange={(v) => setForm({...form, full_name: v})} required />
          <Input label="Email *" type="email" value={form.email} onChange={(v) => setForm({...form, email: v})} required />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Phone *" value={form.phone} onChange={(v) => setForm({...form, phone: v})} required />
          <Input label="Company" value={form.company_name} onChange={(v) => setForm({...form, company_name: v})} />
        </div>
        <Input label="Address" value={form.address} onChange={(v) => setForm({...form, address: v})} />
        <div className="grid grid-cols-3 gap-4">
          <Input label="City" value={form.city} onChange={(v) => setForm({...form, city: v})} />
          <Input label="State" value={form.state} onChange={(v) => setForm({...form, state: v})} />
          <Input label="ZIP" value={form.zip_code} onChange={(v) => setForm({...form, zip_code: v})} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Select label="Client Type" value={form.client_type} onChange={(v) => setForm({...form, client_type: v})} options={[
            { value: 'prospect', label: 'Prospect' },
            { value: 'active', label: 'Active Client' },
            { value: 'inactive', label: 'Inactive' },
          ]} />
          <Select label="Where Met" value={form.where_met} onChange={(v) => setForm({...form, where_met: v})} options={[
            { value: '', label: 'Select where you met' },
            ...WHERE_MET_OPTIONS.map(opt => ({ value: opt, label: opt }))
          ]} />
        </div>
        
        {/* Service Tags */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">Service Interest</label>
          <div className="flex flex-wrap gap-2">
            {SERVICE_TAGS.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleServiceTag(tag)}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  (form.service_tags || []).includes(tag)
                    ? 'bg-[#FF5E00] text-white'
                    : 'bg-[#1a3a6e] text-[#b8c9e2] hover:bg-[#234b8a]'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
        
        <Textarea label="Notes" value={form.notes} onChange={(v) => setForm({...form, notes: v})} />
        <div className="flex justify-end gap-2 pt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-white hover:bg-white/10 rounded">Cancel</button>
          <button type="submit" disabled={loading} className="px-4 py-2 bg-[#FF5E00] hover:bg-[#e05300] text-white rounded disabled:opacity-50">
            {loading ? 'Saving...' : 'Save Client'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const VendorModal = ({ vendor, token, onClose, onSave }) => {
  const [form, setForm] = useState(vendor || {
    company_name: '', contact_name: '', email: '', phone: '', address: '',
    city: '', state: '', zip_code: '', website: '', category: '',
    services_provided: [], notes: '', rating: null, status: 'active'
  });
  const [newService, setNewService] = useState('');
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (vendor) {
        await axios.put(`${API}/admin/vendors/${vendor.id}`, form, { headers });
        toast.success('Vendor updated');
      } else {
        await axios.post(`${API}/admin/vendors`, form, { headers });
        toast.success('Vendor created');
      }
      onSave();
    } catch (error) {
      toast.error('Failed to save vendor');
    } finally {
      setLoading(false);
    }
  };

  const addService = () => {
    if (newService.trim() && !form.services_provided.includes(newService.trim())) {
      setForm({ ...form, services_provided: [...form.services_provided, newService.trim()] });
      setNewService('');
    }
  };

  const removeService = (service) => {
    setForm({ ...form, services_provided: form.services_provided.filter(s => s !== service) });
  };

  return (
    <Modal title={vendor ? 'Edit Vendor' : 'Add Vendor'} onClose={onClose} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input label="Company Name *" value={form.company_name} onChange={(v) => setForm({...form, company_name: v})} required />
          <Input label="Contact Name" value={form.contact_name} onChange={(v) => setForm({...form, contact_name: v})} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({...form, email: v})} />
          <Input label="Phone" value={form.phone} onChange={(v) => setForm({...form, phone: v})} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Website" value={form.website} onChange={(v) => setForm({...form, website: v})} />
          <Select label="Category *" value={form.category} onChange={(v) => setForm({...form, category: v})} required options={[
            { value: '', label: 'Select category' },
            ...VENDOR_CATEGORIES.map(cat => ({ value: cat, label: cat }))
          ]} />
        </div>
        <Input label="Address" value={form.address} onChange={(v) => setForm({...form, address: v})} />
        <div className="grid grid-cols-3 gap-4">
          <Input label="City" value={form.city} onChange={(v) => setForm({...form, city: v})} />
          <Input label="State" value={form.state} onChange={(v) => setForm({...form, state: v})} />
          <Input label="ZIP" value={form.zip_code} onChange={(v) => setForm({...form, zip_code: v})} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Select label="Status" value={form.status} onChange={(v) => setForm({...form, status: v})} options={[
            { value: 'active', label: 'Active' },
            { value: 'preferred', label: 'Preferred' },
            { value: 'inactive', label: 'Inactive' },
          ]} />
          <Select label="Rating" value={form.rating || ''} onChange={(v) => setForm({...form, rating: v ? parseInt(v) : null})} options={[
            { value: '', label: 'No rating' },
            { value: '1', label: '1 Star' },
            { value: '2', label: '2 Stars' },
            { value: '3', label: '3 Stars' },
            { value: '4', label: '4 Stars' },
            { value: '5', label: '5 Stars' },
          ]} />
        </div>
        
        {/* Services Provided */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">Services Provided</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newService}
              onChange={(e) => setNewService(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
              placeholder="Add a service..."
              className="flex-1 px-3 py-2 bg-[#234b8a] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#FF5E00]"
            />
            <button type="button" onClick={addService} className="px-3 py-2 bg-[#FF5E00] text-white rounded">
              <Plus size={16} />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(form.services_provided || []).map((service, i) => (
              <span key={i} className="px-2 py-1 bg-[#1a3a6e] text-[#b8c9e2] text-sm rounded flex items-center gap-1">
                {service}
                <button type="button" onClick={() => removeService(service)} className="text-red-400 hover:text-red-300">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
        
        <Textarea label="Notes" value={form.notes} onChange={(v) => setForm({...form, notes: v})} />
        <div className="flex justify-end gap-2 pt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-white hover:bg-white/10 rounded">Cancel</button>
          <button type="submit" disabled={loading} className="px-4 py-2 bg-[#FF5E00] hover:bg-[#e05300] text-white rounded disabled:opacity-50">
            {loading ? 'Saving...' : 'Save Vendor'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const DealModal = ({ deal, clients, token, onClose, onSave }) => {
  const [form, setForm] = useState(deal || {
    client_id: '', title: '', value: 0, stage: 'new_lead', service_type: '', expected_close_date: '', notes: ''
  });
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (deal) {
        await axios.put(`${API}/admin/deals/${deal.id}`, form, { headers });
        toast.success('Deal updated');
      } else {
        await axios.post(`${API}/admin/deals`, form, { headers });
        toast.success('Deal created');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save deal');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this deal?')) return;
    try {
      await axios.delete(`${API}/admin/deals/${deal.id}`, { headers });
      toast.success('Deal deleted');
      onSave();
    } catch (error) {
      toast.error('Failed to delete deal');
    }
  };

  return (
    <Modal title={deal ? 'Edit Deal' : 'Add Deal'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select label="Client *" value={form.client_id} onChange={(v) => setForm({...form, client_id: v})} required options={[
          { value: '', label: 'Select client' },
          ...clients.map(c => ({ value: c.id, label: `${c.full_name}${c.company_name ? ` (${c.company_name})` : ''}` }))
        ]} />
        <Input label="Deal Title *" value={form.title} onChange={(v) => setForm({...form, title: v})} required />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Value ($)" type="number" value={form.value} onChange={(v) => setForm({...form, value: parseFloat(v) || 0})} />
          <Select label="Stage" value={form.stage} onChange={(v) => setForm({...form, stage: v})} options={PIPELINE_STAGES.map(s => ({ value: s.id, label: s.label }))} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Select label="Service Type" value={form.service_type} onChange={(v) => setForm({...form, service_type: v})} options={[
            { value: '', label: 'Select service' },
            ...SERVICE_TAGS.map(s => ({ value: s, label: s }))
          ]} />
          <Input label="Expected Close Date" type="date" value={form.expected_close_date} onChange={(v) => setForm({...form, expected_close_date: v})} />
        </div>
        <Textarea label="Notes" value={form.notes} onChange={(v) => setForm({...form, notes: v})} />
        <div className="flex justify-between pt-4">
          {deal && (
            <button type="button" onClick={handleDelete} className="px-4 py-2 text-red-400 hover:bg-red-500/10 rounded">Delete</button>
          )}
          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={onClose} className="px-4 py-2 text-white hover:bg-white/10 rounded">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-[#FF5E00] hover:bg-[#e05300] text-white rounded disabled:opacity-50">
              {loading ? 'Saving...' : 'Save Deal'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

const TaskModal = ({ clients, token, onClose, onSave }) => {
  const [form, setForm] = useState({ client_id: '', title: '', description: '', due_date: '', priority: 'medium' });
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/tasks`, form, { headers });
      toast.success('Task created');
      onSave();
    } catch (error) {
      toast.error('Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Add Task" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Task Title *" value={form.title} onChange={(v) => setForm({...form, title: v})} required />
        <Textarea label="Description" value={form.description} onChange={(v) => setForm({...form, description: v})} />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Due Date" type="date" value={form.due_date} onChange={(v) => setForm({...form, due_date: v})} />
          <Select label="Priority" value={form.priority} onChange={(v) => setForm({...form, priority: v})} options={[
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
          ]} />
        </div>
        <Select label="Related Client" value={form.client_id} onChange={(v) => setForm({...form, client_id: v})} options={[
          { value: '', label: 'None' },
          ...clients.map(c => ({ value: c.id, label: c.full_name }))
        ]} />
        <div className="flex justify-end gap-2 pt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-white hover:bg-white/10 rounded">Cancel</button>
          <button type="submit" disabled={loading} className="px-4 py-2 bg-[#FF5E00] hover:bg-[#e05300] text-white rounded disabled:opacity-50">
            {loading ? 'Saving...' : 'Create Task'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const ActivityModal = ({ clients, token, onClose, onSave }) => {
  const [form, setForm] = useState({ client_id: '', activity_type: 'call', description: '' });
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/activities`, form, { headers });
      toast.success('Activity logged');
      onSave();
    } catch (error) {
      toast.error('Failed to log activity');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Log Activity" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select label="Client *" value={form.client_id} onChange={(v) => setForm({...form, client_id: v})} required options={[
          { value: '', label: 'Select client' },
          ...clients.map(c => ({ value: c.id, label: c.full_name }))
        ]} />
        <Select label="Activity Type" value={form.activity_type} onChange={(v) => setForm({...form, activity_type: v})} options={[
          { value: 'call', label: 'Phone Call' },
          { value: 'email', label: 'Email' },
          { value: 'meeting', label: 'Meeting' },
          { value: 'note', label: 'Note' },
        ]} />
        <Textarea label="Description *" value={form.description} onChange={(v) => setForm({...form, description: v})} required />
        <div className="flex justify-end gap-2 pt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-white hover:bg-white/10 rounded">Cancel</button>
          <button type="submit" disabled={loading} className="px-4 py-2 bg-[#FF5E00] hover:bg-[#e05300] text-white rounded disabled:opacity-50">
            {loading ? 'Saving...' : 'Log Activity'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ============== UI COMPONENTS ==============

const Modal = ({ title, children, onClose, size = 'md' }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className={`bg-[#1a3a6e] border border-white/20 w-full max-h-[90vh] overflow-y-auto ${size === 'lg' ? 'max-w-2xl' : 'max-w-lg'}`}>
      <div className="flex items-center justify-between p-4 border-b border-white/20">
        <h3 className="text-lg font-medium text-white">{title}</h3>
        <button onClick={onClose} className="text-[#b8c9e2] hover:text-white">
          <X size={20} />
        </button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);

const Input = ({ label, type = 'text', value, onChange, required }) => (
  <div>
    <label className="block text-sm font-medium text-white mb-1">{label}</label>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full px-3 py-2 bg-[#234b8a] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#FF5E00]"
    />
  </div>
);

const Select = ({ label, value, onChange, options, required }) => (
  <div>
    <label className="block text-sm font-medium text-white mb-1">{label}</label>
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full px-3 py-2 bg-[#234b8a] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#FF5E00]"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const Textarea = ({ label, value, onChange, required }) => (
  <div>
    <label className="block text-sm font-medium text-white mb-1">{label}</label>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      rows={3}
      className="w-full px-3 py-2 bg-[#234b8a] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#FF5E00] resize-none"
    />
  </div>
);

export default CRMDashboard;
